import {
  AvailableAscendQuestLookups,
  DeprecatedAscendQuestLookups,
} from './contracts';
import { firstNonEmptyOrDefault } from '../../../../utils';
import {
  AttributesFromFullAttribute,
  InputLabel,
  InputLabelsMap,
} from '../../fields';
import {
  AttributeLookup,
  BundleAttributesLookup,
  BundleLookup,
  DemographicLookup,
  HasNameAndCode,
  Lookup,
  ScoreLookup,
  AddonProductLookup,
  TrendviewLookup,
} from '../../lookups';
import { SSARProgram } from '../../programs';
import {
  AscendQuestDataDictionarySchema,
  AscendQuestDataDictionarySchemaForSubcode,
  convertToDemographicsCodes,
} from '../../services';
import { areAttributesSame, doesSubcodeHaveAccess } from '../../utils';

export const getAttributeLookupsFromAscendQuestDataDictionary = (
  dataDictionary: AscendQuestDataDictionarySchema,
  includeDisabled = false,
) => {
  return dataDictionary.attributeGroups.reduce(
    (accGrp: AttributeLookup[], currGrp) => {
      return accGrp.concat(
        currGrp.categories?.reduce(
          (accCat: AttributeLookup[], currCat) =>
            accCat.concat(
              currCat.attributes?.reduce(
                (accAtt: AttributeLookup[], currAtt) => {
                  const isDisabled = currAtt.isActive === 'NO';
                  if (!includeDisabled && isDisabled) {
                    return accAtt;
                  }
                  return accAtt.concat(
                    currAtt.outputFields.map((outputField) => {
                      const attributeLookup: AttributeLookup = {
                        group: currGrp.name,
                        category: currCat.name,
                        code: currAtt?.code || outputField.name,
                        name: currAtt?.name || outputField.name,
                        description:
                          outputField.description || currAtt.description || '',
                        isDisabled,
                        exclusiveSubCodes: firstNonEmptyOrDefault(
                          currGrp.exclusiveSubCodes,
                          currCat.exclusiveSubCodes,
                          currAtt.exclusiveSubCodes,
                        ),
                      };
                      return attributeLookup;
                    }),
                  );
                },
                [],
              ) || [],
            ),
          [],
        ) || [],
      );
    },
    [],
  );
};

export const getFullAttributeLookupsFromAscendQuestDataDictionary = (
  dataDictionary: AscendQuestDataDictionarySchema,
) => {
  return dataDictionary.fullAttributes || [];
};

type LookupWithNameAndCode = Lookup & HasNameAndCode;
const extractLookupsFromDataDictionary = <
  T extends Omit<LookupWithNameAndCode, 'isDisabled'> & {
    isActive: string;
  },
>(
  dataDictionary: AscendQuestDataDictionarySchema,
  fn: (dataDictionary: AscendQuestDataDictionarySchema) => T[],
  includeDisabled: boolean,
): LookupWithNameAndCode[] => {
  return fn(dataDictionary)
    .map((a) => {
      const toReturn = {
        code: a.code,
        name: a.name,
        description: a.description,
        isDisabled: a.isActive === 'NO',
        exclusiveSubCodes: a.exclusiveSubCodes || [],
      };
      return toReturn;
    })
    .filter((a) => {
      return includeDisabled || !a.isDisabled;
    });
};

export const getBundleLookupsFromAscendQuestDataDictionary = (
  dataDictionary: AscendQuestDataDictionarySchema,
  includeDisabled = false,
): BundleLookup[] => {
  return extractLookupsFromDataDictionary(
    dataDictionary,
    (a) => a.bundles,
    includeDisabled,
  );
};

export const getBundleAttributeLookupsFromAscendQuestDataDictionary = (
  dataDictionary: AscendQuestDataDictionarySchema,
): BundleAttributesLookup[] => {
  return dataDictionary.bundles.map((a) => {
    const bundleAttributes: BundleAttributesLookup = {
      code: a.code,
      attributeCodes: a.outputFields.map((o) => o.name),
    };
    return bundleAttributes;
  });
};

export const getAttributesForFullAttributeFromAscendQuestDataDictionary = (
  dataDictionary: AscendQuestDataDictionarySchema,
  includeDisabled = false,
): AttributesFromFullAttribute[] => {
  return (
    dataDictionary.fullAttributes?.map((fullAttribute) => {
      const groups = fullAttribute.attributeGroups;
      const attributeCodes: string[] =
        dataDictionary.attributeGroups
          ?.filter((attributeGroup) => groups.includes(attributeGroup.name))
          ?.flatMap((attributeGroup) => {
            return (
              attributeGroup.categories?.flatMap(
                (category) =>
                  category.attributes
                    ?.filter(
                      (attribute) =>
                        includeDisabled || attribute.isActive !== 'NO',
                    )
                    ?.map((attribute) => attribute.code) || [],
              ) || []
            );
          }) || [];
      return { code: fullAttribute.code, attributeCodes: attributeCodes };
    }) || []
  );
};

export const getFullAttributeNamesFromAscendQuestDataDictionary = (
  dataDictionary: AscendQuestDataDictionarySchema,
): string[] => {
  return (dataDictionary.fullAttributes || []).map(
    (fullAttribute) => fullAttribute.displayName,
  );
};

export const getDemographicsLookupsFromAscendQuestDataDictionary = (
  dataDictionary: AscendQuestDataDictionarySchema,
  includeDisabled = false,
): DemographicLookup[] => {
  return extractLookupsFromDataDictionary(
    dataDictionary,
    (a) => a.demographics,
    includeDisabled,
  );
};

export const getInputLabelsFromAscendQuestDataDictionary = (
  dataDictionary: AscendQuestDataDictionarySchema,
): InputLabel[] => {
  return dataDictionary.inputLabels;
};

export const getInputLabelsMapFromAscendQuestDataDictionary = (
  dataDictionary: AscendQuestDataDictionarySchema,
): InputLabelsMap => {
  return getInputLabelsFromAscendQuestDataDictionary(dataDictionary).reduce(
    (acc, curr) => {
      acc[curr.name.toLowerCase()] = curr;
      return acc;
    },
    {} as InputLabelsMap,
  );
};

export const getScoreLookupsFromAscendQuestDataDictionary = (
  dataDictionary: AscendQuestDataDictionarySchema,
  includeDisabled = false,
): ScoreLookup[] => {
  return extractLookupsFromDataDictionary(
    dataDictionary,
    (a) => a.scores,
    includeDisabled,
  );
};

export const getTrendviewLookupsFromAscendQuestDataDictionary = (
  dataDictionary: AscendQuestDataDictionarySchema,
  includeDisabled = false,
): TrendviewLookup[] => {
  return extractLookupsFromDataDictionary(
    dataDictionary,
    (a) => a.trendviewFields,
    includeDisabled,
  );
};

export const getAddonProductLookupsFromAscendQuestDataDictionary = (
  dataDictionary: AscendQuestDataDictionarySchema,
  includeDisabled = false,
): AddonProductLookup[] => {
  return dataDictionary?.addonProducts?.flatMap((addonProduct) => {
    const toReturn: AddonProductLookup[] = (addonProduct.products || [])
      .map(({ isActive, ...product }) => ({
        ...product,
        group: addonProduct.name,
        groupDescription: addonProduct?.description || '',
        isDisabled: isActive === 'NO',
        exclusiveSubCodes: product.exclusiveSubCodes || [],
      }))
      ?.filter((a) => {
        return includeDisabled || !a.isDisabled;
      });
    return toReturn;
  });
};

export const toAvailableAscendQuestLookups = (
  dataDictionaryForSubCode: AscendQuestDataDictionarySchemaForSubcode,
  includeDisabled = false,
): AvailableAscendQuestLookups => {
  const { schema: dataDictionary } = dataDictionaryForSubCode;
  const toReturn: AvailableAscendQuestLookups = {
    attributesLookups: getAttributeLookupsFromAscendQuestDataDictionary(
      dataDictionary,
      includeDisabled,
    ),
    bundleAttributes:
      getBundleAttributeLookupsFromAscendQuestDataDictionary(dataDictionary),
    attributesFromFullAttribute:
      getAttributesForFullAttributeFromAscendQuestDataDictionary(
        dataDictionary,
        includeDisabled,
      ),
    fullAttributesLookups:
      getFullAttributeLookupsFromAscendQuestDataDictionary(dataDictionary),
    bundlesLookups: getBundleLookupsFromAscendQuestDataDictionary(
      dataDictionary,
      includeDisabled,
    ),
    demographicsLookups: getDemographicsLookupsFromAscendQuestDataDictionary(
      dataDictionary,
      includeDisabled,
    ),
    inputLabelsMap:
      getInputLabelsMapFromAscendQuestDataDictionary(dataDictionary),
    scoresLookups: getScoreLookupsFromAscendQuestDataDictionary(
      dataDictionary,
      includeDisabled,
    ),
    trendviewLookups: getTrendviewLookupsFromAscendQuestDataDictionary(
      dataDictionary,
      includeDisabled,
    ),
    addonProductsLookups: getAddonProductLookupsFromAscendQuestDataDictionary(
      dataDictionary,
      includeDisabled,
    ),
  };
  return toReturn;
};

const shouldDisableOrExclude = (
  input: {
    isDisabled: boolean;
    exclusiveSubCodes?: string[];
    code: string;
  },
  subCode: string,
): boolean => {
  return (
    input.isDisabled || !doesSubcodeHaveAccess(subCode, input.exclusiveSubCodes)
  );
};

const getDeprecatedLookups =
  (
    asOfLookups: AvailableAscendQuestLookups,
    currentLookups: AvailableAscendQuestLookups,
    subCode: string,
  ) =>
  <T, U extends LookupWithNameAndCode>(
    dataPart: T[] | undefined,
    lookupsPartExtractor: (lookups: AvailableAscendQuestLookups) => U[],
    comparerFn: (t: T, u: U) => boolean,
  ) => {
    const toReturn: U[] = (dataPart || [])
      .filter((a) => {
        const currentPartLookups = lookupsPartExtractor(currentLookups);
        const isInCurrent = currentPartLookups.some(
          (b) => comparerFn(a, b) && !shouldDisableOrExclude(b, subCode),
        );
        return !isInCurrent;
      })
      .map((a) => {
        const asOfPartLookups = lookupsPartExtractor(asOfLookups);
        const prevLookup = asOfPartLookups.find((b) => comparerFn(a, b));
        return prevLookup;
      })
      .filter((a) => !!a)
      .map((a) => {
        if (!a) {
          throw new Error('This Should never happen');
        }
        return a;
      });
    return toReturn;
  };

export const getAvailableLookupsFromDataDictionary = (
  dataDictionaryForSubcode: AscendQuestDataDictionarySchemaForSubcode,
): AvailableAscendQuestLookups => {
  const currentLookups: AvailableAscendQuestLookups =
    toAvailableAscendQuestLookups(dataDictionaryForSubcode);
  return currentLookups;
};

export const getEmptyDeprecatedAscendQuestLookups =
  (): DeprecatedAscendQuestLookups => ({
    attributesLookups: [],
    bundlesLookups: [],
    demographicsLookups: [],
    scoresLookups: [],
    trendviewLookups: [],
    addonProductsLookups: [],
  });

export const getEmptyAvailableAscendQuestLookups =
  (): AvailableAscendQuestLookups => {
    return {
      scoresLookups: [],
      trendviewLookups: [],
      addonProductsLookups: [],
      demographicsLookups: [],
      bundlesLookups: [],
      fullAttributesLookups: [],
      attributesFromFullAttribute: [],
      attributesLookups: [],
      bundleAttributes: [],
      inputLabelsMap: {},
      outputLayoutLookups: [],
    };
  };

export const getDeprecatedLookupsForProgram = (
  currentDataDictionaryForSubcode: AscendQuestDataDictionarySchemaForSubcode,
  asOfDataDictionaryForSubcode: AscendQuestDataDictionarySchemaForSubcode,
  program?: SSARProgram,
): DeprecatedAscendQuestLookups => {
  if (asOfDataDictionaryForSubcode === currentDataDictionaryForSubcode) {
    return getEmptyDeprecatedAscendQuestLookups();
  }
  const currentLookups = getAvailableLookupsFromDataDictionary(
    currentDataDictionaryForSubcode,
  );
  const asOfLookups: AvailableAscendQuestLookups =
    toAvailableAscendQuestLookups(asOfDataDictionaryForSubcode, true);

  const disabledLookups: DeprecatedAscendQuestLookups =
    getEmptyDeprecatedAscendQuestLookups();

  if (program) {
    const getDeprecatedLookupsFn = getDeprecatedLookups(
      asOfLookups,
      currentLookups,
      program.subCode,
    );

    disabledLookups.attributesLookups = getDeprecatedLookupsFn(
      program.attributesAndBundles?.attributes,
      (a) => a.attributesLookups,
      (a, b) => {
        return areAttributesSame(a, b);
      },
    );
    disabledLookups.bundlesLookups = getDeprecatedLookupsFn(
      program.attributesAndBundles?.bundles,
      (a) => a.bundlesLookups,
      (a, b) => a === b.code,
    );
    disabledLookups.demographicsLookups = getDeprecatedLookupsFn(
      convertToDemographicsCodes(program.demographics),
      (a) => a.demographicsLookups,
      (a, b) => a === b.code,
    );
    disabledLookups.scoresLookups = getDeprecatedLookupsFn(
      program.scoresAndAddonProducts?.scores,
      (a) => a.scoresLookups,
      (a, b) => a === b.code,
    );
    disabledLookups.trendviewLookups = getDeprecatedLookupsFn(
      program.scoresAndAddonProducts?.trendviewFields,
      (a) => a.trendviewLookups,
      (a, b) => a === b.code,
    );
    disabledLookups.addonProductsLookups = getDeprecatedLookupsFn(
      program.scoresAndAddonProducts?.addonProducts,
      (a) => a.addonProductsLookups,
      (a, b) => a === b.code,
    );
  }
  return disabledLookups;
};
